export const collectionChecklist = [
  'Functional AAC & generative language interventions',
  'Complex grammar, syntax & narrative activities',
  'Communication repair, self-advocacy & autonomy',
  'Eye gaze, scanning, switch & alternative access tasks',
  'Real-world AAC participation programs',
  'Complex clinical cases & decision pathways',
  'AAC assessment & feature-matching tools',
  'Printable communication boards & vocabulary',
  'Communication partner coaching materials',
  'Data collection & progress tracking tools',
  'Emergency AAC & backup communication resources',
];

export const problemCards = [
  {
    variant: 'blue' as const,
    title: 'What if your advanced AAC materials were already organized?',
    paragraphs: [
      'Effective AAC intervention goes far beyond selecting pictures or teaching basic requests. Every user brings different language needs, access methods, and participation goals — and finding advanced materials for each one takes time.',
      'Complete AAC Communication Toolkit™ brings together six specialized clinical volumes and five professional bonuses, built around the real demands of advanced AAC practice.',
    ],
    highlight: 'Organize. Select. Adapt. Implement.',
  },
  {
    variant: 'gold' as const,
    title: 'Spend less time creating materials for every clinical challenge.',
    paragraphs: [
      'Your time belongs with your clients — not creating visual scenarios, language stimuli, partner instructions, and clinical forms from scratch.',
      'Choose the volume. Identify the target. Select the resource. Adapt it to the individual.',
    ],
    highlight: 'Less repetitive preparation. More organized professional materials.',
  },
  {
    variant: 'navy' as const,
    title: 'Less searching. More ready-to-use clinical resources.',
    paragraphs: [
      'Spontaneous message generation, complex language and narrative, communication repair and self-advocacy, eye gaze and switch access, and participation across school, healthcare, home, and community — plus clinical cases and five professional bonuses.',
    ],
    highlight: 'Open. Select. Adapt. Implement.',
  },
];

export const volumes = [
  {
    number: '01',
    title: 'Advanced Functional AAC Communication & Generative Language',
    subtitle: '40 Pages of Advanced Functional AAC Activities',
    points: [
      'Structured activities for communication initiation, complex preferences, information-seeking, partner-directed tasks, and original message generation — with visual scenarios and clinician procedures that go beyond predetermined answers.',
    ],
    finalLine: 'Initiate. Generate. Communicate. Participate.',
  },
  {
    number: '02',
    title: 'Complex AAC Language Structures, Grammar & Narrative',
    subtitle: '40 Pages of Advanced Language and Narrative Materials',
    points: [
      'Picture sequences, sentence construction, narrative tasks, and linguistic contrasts targeting morphology, complex syntax, referential language, temporal and causal relationships, and connected discourse.',
    ],
    finalLine: 'Structure. Connect. Express. Narrate.',
  },
  {
    number: '03',
    title: 'Advanced AAC Self-Advocacy, Communication Repair & Autonomy',
    subtitle: '40 Pages of Specialized Communication Autonomy Interventions',
    points: [
      'Interaction scenarios and partner response cards for breakdowns, clarification, refusal, boundaries, disagreement, supported decision-making, and preserving message authorship — always respecting consent.',
    ],
    finalLine: 'Clarify. Decide. Advocate. Communicate.',
  },
  {
    number: '04',
    title: 'Complex AAC Access Methods & Adapted Clinical Interventions',
    subtitle: '40 Pages of Advanced Alternative Access Activities',
    points: [
      'Selection tasks, scanning materials, and partner instructions for eye-pointing, eye gaze, partner-assisted and auditory scanning, switch access, and cursor control — considering motor variability and fatigue.',
    ],
    finalLine: 'Access. Select. Confirm. Adapt.',
  },
  {
    number: '05',
    title: 'Real-World AAC Participation & Generalization Programs',
    subtitle: '40 Pages of Contextual AAC Participation Activities',
    points: [
      'Realistic documents, schedules, service scenarios, and partner role cards for school projects, healthcare visits, household tasks, community services, recreation, and online communication.',
    ],
    finalLine: 'Connect. Contribute. Participate. Generalize.',
  },
  {
    number: '06',
    title: 'Complex AAC Clinical Cases & Problem-Solving Protocols',
    subtitle: '40 Pages Featuring 39 Advanced Fictional Clinical Cases',
    points: [
      'Each case includes a clinical presentation, evidence, competing explanations, decision pathways, and follow-up criteria — covering inconsistent performance, access barriers, progressive needs, and multilingual considerations.',
    ],
    finalLine: 'Analyze. Interpret. Plan. Reassess.',
  },
];

// Pages shown in the "Take a Look Inside" carousel, in order.
export const insidePages = [
  {
    image: 'https://cdn.magicpatterns.com/uploads/tpaTjJVae2wePDVThDfCqX/Captura_de_pantalla_2026-10-21_215928.png',
    alt: 'Activity V01-A17 — Control the Next Action: arrangement-modification task with starting and target object conditions.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/sXTT9MSt5xuQANXyu4bYa7/Captura_de_pantalla_2026-10-21_220039.png',
    alt: 'Activity V01-A18 — Lead the Sequence Through AAC: six-stage procedural coordination flow with branching points.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/gzatYMjbFXXuGruV6YGmoc/Captura_de_pantalla_2026-10-21_220054.png',
    alt: 'Activity V01-A20 — Communicate Actions Across an Information Divide: barrier coordination task layout.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/jPwCa2P3sgb7YgkWx1GYGb/Captura_de_pantalla_2026-10-21_220520.png',
    alt: 'Activity V01-A25 — Report What Has Been Confirmed: verification reporting task with status cards.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/4cRWXz1jb1mpJkEDhQyYW7/Captura_de_pantalla_2026-10-21_220535.png',
    alt: 'Activity V01-A28 — More Than One Way to Communicate Successfully: ten functional situations with equivalent pathways.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/uc4ZhYiWo1M9hKRAcprfZv/Captura_de_pantalla_2026-10-21_220605.png',
    alt: 'Activity V01-A30 — Generate a Message Without a Script: ten illustrated open-ended scenarios.',
  },
];

export const insideResources = [
  { title: 'Functional AAC Activities', text: 'Visual communication challenges with clinician procedures.' },
  { title: 'Language & Narrative Resources', text: 'Sentence, discourse, and narrative materials for AAC.' },
  { title: 'Autonomy Scenarios', text: 'Clarification, boundaries, and user-directed communication.' },
  { title: 'Alternative Access Materials', text: 'Adapted selection tasks and scanning sequences.' },
  { title: 'Participation Activities', text: 'School, home, community, healthcare, and digital contexts.' },
  { title: 'Clinical Case Studies', text: 'Patient profiles, competing hypotheses, and reasoning pathways.' },
];

export const bonuses = [
  {
    number: '01',
    title: 'Advanced AAC Assessment & Feature-Matching Toolkit',
    subtitle: '30 Pages of Assessment and Decision-Making Tools',
    price: 17.9,
    intro: [
      'Trial planning tools, repertoire documentation, access comparisons, evidence forms, and decision matrices to organize feature-matching decisions.',
    ],
    finalLine: 'Observe. Compare. Interpret. Match.',
  },
  {
    number: '02',
    title: 'Customizable AAC Communication Boards & Vocabulary Library',
    subtitle: '30 Pages of Specialized Printable AAC Resources',
    price: 14.9,
    intro: [
      'Boards for abstract relationships, academic discussion, technical concepts, professional communication, and quantitative language — ready to personalize.',
    ],
    finalLine: 'Select. Personalize. Combine. Express.',
  },
  {
    number: '03',
    title: 'Advanced AAC Communication Partner Training & Coaching Toolkit',
    subtitle: '30 Pages of Professional Coaching Resources',
    price: 14.9,
    intro: [
      'Demonstration scripts, role cards, and guided rehearsals to coach families, educators, and assistants in modeling, waiting, and respecting message authorship.',
    ],
    finalLine: 'Demonstrate. Practice. Respond. Support.',
  },
  {
    number: '04',
    title: 'AAC Data Collection, Outcome Measurement & Progress Tracking',
    subtitle: '30 Pages of Professional Documentation Tools',
    price: 16.9,
    intro: [
      'Observation forms, longitudinal displays, and reporting worksheets to track initiation, message generation, support levels, autonomy, and participation.',
    ],
    finalLine: 'Record. Measure. Compare. Review.',
  },
  {
    number: '05',
    title: 'AAC Emergency Communication, Backup Systems & Safety Protocols',
    subtitle: '30 Pages of Emergency and Continuity Resources',
    price: 12.9,
    intro: [
      'Emergency profiles, urgent-message aids, healthcare handoff forms, and backup plans for when established AAC resources are unavailable. Supplementary — does not replace emergency services.',
    ],
    finalLine: 'Prepare. Communicate. Maintain access. Support.',
  },
];

export const benefits = [
  { title: 'Organized by clinical focus', text: 'Six intervention areas and five complementary resource categories.' },
  { title: 'Ready-to-use activities', text: 'Materials, procedures, and visual stimuli ready to adapt.' },
  { title: 'Six specialized areas', text: 'From functional AAC to complex clinical case management.' },
  { title: 'Printable resources', text: 'Print the pages you need and reuse them under the license.' },
  { title: 'Five extra toolkits', text: 'Assessment, boards, coaching, tracking, and emergency resources.' },
  { title: 'More efficient preparation', text: "Select existing materials and adapt them to your client's goals." },
];

export const offerVolumes = [
  'Volume 01 — Functional AAC & Generative Language',
  'Volume 02 — Complex Language, Grammar & Narrative',
  'Volume 03 — Self-Advocacy, Repair & Autonomy',
  'Volume 04 — Complex Access Methods',
  'Volume 05 — Real-World Participation',
  'Volume 06 — Clinical Cases & Problem-Solving',
];

export const offerBonuses = [
  'AAC Assessment & Feature-Matching Toolkit',
  'Communication Boards & Vocabulary Library',
  'Partner Training & Coaching Toolkit',
  'Data Collection & Progress Tracking System',
  'Emergency Communication & Backup Toolkit',
];

export const teachingSituations = [
  {
    title: 'Different communication profiles',
    text: 'Speech-generating devices, low-tech, writing, or multiple modalities — materials adapt to each target.',
  },
  {
    title: 'Beyond basic requesting',
    text: 'Complex expressive language, flexible communication, and self-directed interaction.',
  },
  {
    title: 'Complex access needs',
    text: 'Volume 04 covers eye gaze, scanning, switches, and other adaptations.',
  },
  {
    title: 'Meaningful participation',
    text: 'Volume 05 plans interventions around everyday environments.',
  },
  {
    title: 'Professional documentation',
    text: 'Cases and bonuses support reasoning, assessment, and outcome monitoring.',
  },
];

export const testimonials = [
  {
    name: 'Jennifer M.',
    age: 43,
    role: 'CCC-SLP, AAC specialist · Public school district, Ohio',
    quote:
      "My older students outgrew requesting years ago, and I was rebuilding narrative and repair activities every weekend. Now Volumes 02 and 03 are in my weekly rotation. One of my eye-gaze students led a peer through all eight steps of the information-divide activity — a first for him.",
  },
  {
    name: 'Rachel T.',
    age: 37,
    role: 'Speech pathologist · Pediatric outpatient clinic, Melbourne, Australia',
    quote:
      "The partner coaching bonus alone was worth it. I used the role cards with three families this month, and parents finally understood why waiting matters. The tracking forms also cut my progress report time in half.",
  },
  {
    name: 'David R.',
    age: 51,
    role: 'SLP · Adult neurological rehabilitation unit, Toronto, Canada',
    quote:
      'I work with adults with ALS and brainstem stroke, where access changes week to week. Volume 04 actually accounts for fatigue and unintended activations. I also printed emergency profiles for every patient on our unit.',
  },
];

export const faqs = [
  {
    q: 'Is this a physical product?',
    a: 'No. It is a 100% digital collection in PDF format. Nothing will be shipped.',
  },
  {
    q: 'Who is this toolkit designed for?',
    a: 'Experienced Speech-Language Pathologists, AAC specialists, and qualified professionals working with AAC users.',
  },
  {
    q: 'Is this a course or certification?',
    a: 'No. It is a collection of practical intervention materials, not a certification or substitute for formal AAC training.',
  },
  {
    q: 'Do I need to create the activities myself?',
    a: 'No. Activities, stimuli, scenarios, and procedures are developed. You simply select and individualize them for each client.',
  },
  {
    q: 'How many volumes and bonuses are included?',
    a: 'Six main volumes plus five professional bonuses — 11 resources and 390 pages in total.',
  },
  {
    q: 'Do I need a specific AAC system or device?',
    a: "No. Activities adapt to the individual's established communication and access methods.",
  },
  {
    q: 'Can I print the materials and use them with different clients?',
    a: 'Yes, for professional clinical use according to the license terms included with your purchase.',
  },
  {
    q: 'Are the five bonuses really free?',
    a: 'Yes. All five bonuses are included at no additional charge.',
  },
  {
    q: 'Does this replace a comprehensive AAC assessment?',
    a: 'No. The materials support professional practice but do not replace individualized evaluation or clinical judgment.',
  },
  {
    q: 'How will I receive my purchase?',
    a: "You'll receive digital access instructions right after purchase. Questions? Contact compradigitalyaa@gmail.com.",
  },
];

export const recentBuyers = [
  { name: 'Emily Carter', place: 'Denver, United States', minutes: 3 },
  { name: 'Sofía Martínez', place: 'Madrid, Spain', minutes: 7 },
  { name: 'Hannah O’Connor', place: 'Dublin, Ireland', minutes: 12 },
  { name: 'Chloe Nguyen', place: 'Sydney, Australia', minutes: 5 },
  { name: 'Isabelle Tremblay', place: 'Québec City, Canada', minutes: 16 },
  { name: 'Priya Sharma', place: 'London, United Kingdom', minutes: 9 },
  { name: 'Camila Rojas', place: 'Santiago, Chile', minutes: 14 },
  { name: 'Lena Hoffmann', place: 'Hamburg, Germany', minutes: 6 },
  { name: 'Aroha Williams', place: 'Wellington, New Zealand', minutes: 18 },
  { name: 'Thandi Mokoena', place: 'Cape Town, South Africa', minutes: 11 },
];
